import {createSelector, createSlice, PayloadAction} from '@reduxjs/toolkit'
import {
  ItemEdit,
  ItemEditorMode,
  ShoppingCart,
  ShoppingCartItem
} from '../../types'

const initialState: ShoppingCart = {
  items: [],
  isCartOpen: false,
  isItemEditorOpen: false,
  itemEditorMode: 'Add',
  itemToEdit: null
}

export const shoppingCartSlice = createSlice({
  name: 'shoppingCart',
  initialState,
  reducers: {
    showCart: (state: ShoppingCart) => {
      return {
        ...state,
        isItemEditorOpen: false,
        isCartOpen: true
      }
    },
    hideCart: (state: ShoppingCart) => {
      return {
        ...state,
        isCartOpen: false
      }
    },
    addItem: (state: ShoppingCart, action: PayloadAction<ShoppingCartItem>) => {
      return {
        ...state,
        items: [...state.items, action.payload]
      }
    },
    updateItem: (
      state: ShoppingCart,
      action: PayloadAction<ShoppingCartItem>
    ) => {
      const index = state.items.findIndex(item => item.id === action.payload.id)
      if (index >= 0) {
        state.items[index] = action.payload
      }
    },
    updateAllItems: (
      state: ShoppingCart,
      action: PayloadAction<ShoppingCartItem[]>
    ) => {
      return {
        ...state,
        items: action.payload
      }
    },
    removeItem: (state: ShoppingCart, action: PayloadAction<string>) => {
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      }
    },
    showItemEditor: (state: ShoppingCart, action: PayloadAction<ItemEdit>) => {
      return {
        ...state,
        isCartOpen: false,
        isItemEditorOpen: true,
        itemEditorMode: action.payload?.mode,
        itemToEdit: action.payload.item
      }
    },
    updateItemEditor: (
      state: ShoppingCart,
      action: PayloadAction<ShoppingCartItem>
    ) => {
      return {
        ...state,
        itemToEdit: action.payload
      }
    },
    hideItemEditor: (state: ShoppingCart) => {
      return {
        ...state,
        isItemEditorOpen: false,
        itemToEdit: null
      }
    },
    clearCart: (state: ShoppingCart) => {
      return {
        ...state,
        items: []
      }
    }
  }
})

// selectors
export const selectCartItems = (state: any): ShoppingCartItem[] => {
  return state.shoppingCart?.items || []
}

export const selectCartItemCount = createSelector(
  selectCartItems,
  (items: ShoppingCartItem[]): number => {
    return items?.length || 0
  }
)

export const selectCartHasItems = createSelector(
  selectCartItemCount,
  (count: number): boolean => {
    return count > 0
  }
)

export const selectIsCartOpen = (state: any): boolean => {
  return state.shoppingCart?.isCartOpen || false
}

export const selectIsItemEditorOpen = (state: any): boolean => {
  return state.shoppingCart?.isItemEditorOpen || false
}

export const selectItemToEdit = (state: any): ShoppingCartItem | null => {
  return state.shoppingCart?.itemToEdit
}

export const selectItemEditorMode = (state: any): ItemEditorMode => {
  return state.shoppingCart?.itemEditorMode
}

/**
 * Checks if there are items in the cart that are member rates
 */
export const selectHasMemberRate = createSelector([selectCartItems], items => {
  return items.some(item => {
    return item.offer.isMemberRate === true
  })
})

/**
 * Gets the property IDs of the cart items
 */
export const selectPropertyIds = createSelector(
  selectCartItems,
  (items: ShoppingCartItem[]): string[] => {
    const properties: string[] = []
    for (const item of items) {
      if (!properties.includes(item.propertyId)) {
        properties.push(item.propertyId)
      }
    }
    return properties
  }
)

export const selectPropertyId = createSelector(
  selectCartItems,
  (items: ShoppingCartItem[]): string => {
    return items?.[0]?.propertyId
  }
)

/**
 * Gets the property ID of the cart items
 */
export const selectCartItemsProperty = createSelector(
  selectCartItems,
  items => {
    return items?.length > 0 ? items[0].propertyId : ''
  }
)

/**
 * Checks if there are items in the cart that are from multiple properties
 */
export const selectIsMultiProperty = createSelector(
  selectPropertyIds,
  (propertyIds: string[]): boolean => {
    return propertyIds.length > 1
  }
)

/**
 * Gets shopping cart items grouped by property
 */
export const selectCartItemsByProperty = createSelector(
  selectCartItems,
  (items: ShoppingCartItem[]): {[key: string]: ShoppingCartItem[]} => {
    return items.reduce((acc, item) => {
      if (!acc[item.propertyId]) {
        acc[item.propertyId] = []
      }
      acc[item.propertyId].push(item)
      return acc
    }, {} as {[key: string]: ShoppingCartItem[]})
  }
)

// actions
export const {
  showCart,
  hideCart,
  clearCart,
  addItem,
  updateItem,
  updateAllItems,
  removeItem,
  showItemEditor,
  updateItemEditor,
  hideItemEditor
} = shoppingCartSlice.actions

// reducer
export default shoppingCartSlice.reducer
